import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, MessageCircle, ArrowUpRight, Phone, Mail, MapPin, Globe } from "lucide-react";

const SOCIALS = [
  { Icon: Facebook, href: "#", label: "Facebook", soon: true },
  { Icon: Instagram, href: "#", label: "Instagram", soon: true },
  { Icon: Youtube, href: "#", label: "YouTube", soon: true },
  { Icon: MessageCircle, href: "https://wa.me/917424848477", label: "WhatsApp" },
];

const NAV = [
  { to: "/our-story", label: "Our Story" },
  { to: "/destinations", label: "Study Destinations" },
  { to: "/ecosystem", label: "Medics Ecosystem" },
  { to: "/beware-scams", label: "Beware of Scams" },
  { to: "/contact", label: "Contact Us" },
];

const CONTACT = [
  { Icon: Phone, v: "+91 74248 48477", href: "tel:+917424848477" },
  { Icon: Mail, v: "enquiries@wurkwise.com", href: "mailto:enquiries@wurkwise.com" },
  { Icon: Globe, v: "medics.wurkwise.com", href: "https://www.medics.wurkwise.com" },
  { Icon: MapPin, v: "Sector 68, Noida 201307" },
];

export const SiteFooter = () => (
  <footer className="relative bg-space text-white" data-testid="site-footer">
    <div className="h-px w-full bg-gradient-to-r from-transparent via-[var(--blue)]/45 to-transparent" />
    <div className="relative mx-auto max-w-[1500px] px-5 py-16 sm:px-8 lg:px-10">
      {/* Brand + CTA */}
      <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="font-head text-3xl tracking-tight">
            <span className="text-[var(--blue)]">Wurkwise</span>{" "}
            <span className="text-[var(--lime)]">Medics.</span>
          </div>
          <p className="mt-1.5 text-xs uppercase tracking-[0.22em] text-white/40" data-testid="footer-unit">
            A unit of Wurkwise Group
          </p>
          <p className="mt-3 max-w-sm text-sm text-white/50">
            Beside every future doctor, from first counselling to the white coat, and beyond.
          </p>
        </div>
        <Link
          to="/contact"
          className="group inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-[var(--blue)]/60 hover:bg-[var(--blue)]/10"
          data-testid="footer-cta"
        >
          Book Counselling
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>

      {/* Nav + Socials */}
      <div className="mt-12 flex flex-col gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center md:justify-between">
        <nav className="flex flex-wrap gap-x-7 gap-y-3">
          {NAV.map((l) => (
            <Link key={l.to} to={l.to} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={`footer-link-${l.to.replace("/", "")}`}>
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-4" data-testid="footer-socials">
          <div className="relative flex gap-2.5">
            <span
              className="pointer-events-none absolute -top-7 left-0 whitespace-nowrap rounded-full border border-[var(--lime)]/30 bg-[var(--lime)]/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--lime)]"
              data-testid="social-coming-soon"
            >
              Coming soon
            </span>
            {SOCIALS.filter((s) => s.soon).map((s) => (
              <span
                key={s.label}
                aria-label={`${s.label} (coming soon)`}
                data-testid={`social-${s.label.toLowerCase()}`}
                className="flex h-9 w-9 cursor-default items-center justify-center rounded-full border border-white/12 text-white/40 opacity-70"
              >
                <s.Icon className="h-4 w-4" />
              </span>
            ))}
          </div>
          {SOCIALS.filter((s) => !s.soon).map((s) => (
            <a
              key={s.label}
              href={s.href}
              target={s.href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              aria-label={s.label}
              data-testid={`social-${s.label.toLowerCase()}`}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/12 text-white/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--blue)]/60 hover:text-[var(--blue)]"
            >
              <s.Icon className="h-4 w-4" />
            </a>
          ))}
        </div>
      </div>

      {/* Contact row */}
      <div className="mt-8 flex flex-col gap-3 text-sm text-white/45 md:flex-row md:flex-wrap md:items-center md:gap-x-8" data-testid="footer-contact">
        {CONTACT.map((c, i) =>
          c.href ? (
            <a key={i} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center gap-2 transition-colors hover:text-white">
              <c.Icon className="h-3.5 w-3.5 text-[var(--blue)]" /> {c.v}
            </a>
          ) : (
            <span key={i} className="flex items-center gap-2">
              <c.Icon className="h-3.5 w-3.5 text-[var(--blue)]" /> {c.v}
            </span>
          )
        )}
      </div>

      {/* Bottom */}
      <div className="mt-10 flex flex-col items-start justify-between gap-2 border-t border-white/10 pt-6 text-xs text-white/35 sm:flex-row sm:items-center">
        <span>© {new Date().getFullYear()} Wurkwise Medics. All rights reserved.</span>
        <span>A-5, Grovy Optiva, Block A, Sector 68, Noida 201307</span>
      </div>
    </div>
  </footer>
);
